// Author credits for Google Places photos, keyed off the image URL itself.
//
// Google requires the author's name, profile link and avatar wherever one of
// its photos is shown, plus a way to open the photo on Google Maps
// (docs/GOOGLE_ATTRIBUTION.md rule 3). The cover cascade hands us only a proxy
// URL for a Google photo — /api/places/photo?photo_reference=…&place_id=… — so
// the credit is looked up from that URL, which guarantees it belongs to the
// image actually on screen. (The `googlePhotoAttribution` field some responses
// carry is always photos[0]'s author, whatever the cover turned out to be.)
//
// Lookups are batched: every request made within BATCH_DELAY_MS goes out as
// one POST, so a search page full of Google photos costs one or two calls, not
// one per card. Answers (including "no credit") are cached for the session.

import { useEffect, useState } from 'react';
import { getApiUrl } from '../config/api';

const PROXY_PATH = '/api/places/photo';
const BATCH_DELAY_MS = 30;
const MAX_BATCH = 50; // the endpoint's cap
const RETRY_DELAY_MS = 1500;

/**
 * @returns {{ photoReference: string, placeId: string|null } | null} for a
 *   Google photo proxy URL (relative or absolute), null for anything else.
 */
export function parseGooglePhotoUrl(url) {
  if (typeof url !== 'string' || !url.includes(PROXY_PATH)) return null;
  let parsed;
  try {
    parsed = new URL(url, 'http://placeholder.invalid');
  } catch {
    return null;
  }
  if (parsed.pathname !== PROXY_PATH) return null;
  const photoReference = parsed.searchParams.get('photo_reference');
  if (!photoReference) return null;
  return { photoReference, placeId: parsed.searchParams.get('place_id') || null };
}

export const isGooglePhotoUrl = (url) => parseGooglePhotoUrl(url) !== null;

// key: `${photoReference}|${placeId}` → Promise<credit|null>. The place_id is
// part of the key because the proxy decides on it whether to serve a user
// photo instead (in which case there is no Google credit).
const cache = new Map();
let pending = []; // [{ key, photoReference, placeId, resolve, retried }]
let flushTimer = null;

function enqueue(entry, delay = BATCH_DELAY_MS) {
  pending.push(entry);
  if (!flushTimer) flushTimer = setTimeout(flush, delay);
}

async function flush() {
  flushTimer = null;
  const batch = pending.splice(0, MAX_BATCH);
  if (pending.length) flushTimer = setTimeout(flush, 0);
  try {
    const res = await fetch(getApiUrl('/api/places/photo-credits'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        photos: batch.map(({ photoReference, placeId }) => ({ photo_reference: photoReference, place_id: placeId }))
      })
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const { credits = {} } = await res.json();
    batch.forEach(({ photoReference, resolve }) => resolve(credits[photoReference] || null));
  } catch {
    // One retry (a 429 or a dropped connection is usually momentary), then
    // give up without caching, so a remount or a new URL asks again. The hook
    // itself doesn't re-ask on a plain re-render — hence the retry here.
    const [retry, give] = [batch.filter(e => !e.retried), batch.filter(e => e.retried)];
    retry.forEach(entry => enqueue({ ...entry, retried: true }, RETRY_DELAY_MS));
    give.forEach(({ key, resolve }) => {
      cache.delete(key);
      resolve(null);
    });
  }
}

export function fetchGooglePhotoCredit(url) {
  const parsed = parseGooglePhotoUrl(url);
  if (!parsed) return Promise.resolve(null);
  const key = `${parsed.photoReference}|${parsed.placeId || ''}`;
  if (!cache.has(key)) {
    cache.set(key, new Promise(resolve => enqueue({ key, ...parsed, resolve, retried: false })));
  }
  return cache.get(key);
}

/**
 * The credit for the image at `url`, or null (not a Google photo, no credit
 * cached for it, or still loading).
 * @returns {{ author_name, author_uri, author_photo_uri, google_maps_uri } | null}
 */
export function useGooglePhotoCredit(url) {
  const [credit, setCredit] = useState(null);
  useEffect(() => {
    setCredit(null);
    if (!isGooglePhotoUrl(url)) return undefined;
    let active = true;
    fetchGooglePhotoCredit(url).then(c => { if (active) setCredit(c); });
    return () => { active = false; };
  }, [url]);
  return credit;
}
